import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Grid,
  Divider,
  Alert,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fade,
  Slide,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Paper,
  Snackbar,
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  Security as SecurityIcon,
  Notifications as NotificationsIcon,
  Storage as StorageIcon,
  Api as ApiIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import axios from '../../utils/axiosConfig';
import { useAuth } from '../../contexts/AuthContext';

const Settings = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [apiKeyDialogOpen, setApiKeyDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  const { user, updateUser } = useAuth();

  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    company: user?.company || '',
    phone: user?.phone || '',
    avatar: user?.avatar || null,
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [settings, setSettings] = useState({
    // General Settings
    language: 'de',
    timezone: 'Europe/Berlin',
    theme: 'light',
    
    // Notification Settings
    emailNotifications: true,
    pushNotifications: true,
    weeklyReports: true,
    systemAlerts: true,
    
    // Security Settings
    twoFactorAuth: false,
    sessionTimeout: 30,
    loginNotifications: true,
    
    // API Settings
    apiKeys: [],
    openrouterApiKey: '',
    
    // System Settings
    dataRetention: 365,
    logLevel: 'info',
    autoBackup: true,
    backupFrequency: 'daily',
  });

  const [newApiKey, setNewApiKey] = useState({
    name: '',
    key: '',
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      // Lade OpenRouter API Key vom Server
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL || 'https://backend.mtsdemo.de'}/api/global-settings/openrouter_api_key`);
        if (response.data && response.data.value) {
          setSettings(prev => ({
            ...prev,
            openrouterApiKey: response.data.value
          }));
          // Auch in localStorage speichern für bessere Performance
          localStorage.setItem('openrouter_api_key', response.data.value);
        } else {
          // Fallback: Versuche localStorage
          const savedApiKey = localStorage.getItem('openrouter_api_key');
          if (savedApiKey) {
            setSettings(prev => ({
              ...prev,
              openrouterApiKey: savedApiKey
            }));
          }
        }
      } catch (apiError) {
        console.warn('Fehler beim Laden des API Keys vom Server:', apiError);
        // Fallback: Versuche localStorage
        const savedApiKey = localStorage.getItem('openrouter_api_key');
        if (savedApiKey) {
          setSettings(prev => ({
            ...prev,
            openrouterApiKey: savedApiKey
          }));
        }
      }
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      // Settings would be loaded from API here
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Update user profile
      if (activeTab === 0) {
        updateUser(profileData);
      }
      
      // Save OpenRouter API Key
      if (settings.openrouterApiKey) {
        localStorage.setItem('openrouter_api_key', settings.openrouterApiKey);
      }
      
      console.log('Settings saved successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveOpenRouterApiKey = async () => {
    try {
      setSaving(true);
      await axios.put(`${process.env.REACT_APP_API_URL || 'https://backend.mtsdemo.de'}/api/global-settings/openrouter_api_key`, {
        value: settings.openrouterApiKey,
        description: "Globaler OpenRouter API Key für alle Bots",
        is_encrypted: true
      });
      setSnackbar({ open: true, message: 'OpenRouter API Key global gespeichert', severity: 'success' });
      
      // Speichere auch lokal für Frontend-Verwendung
      if (settings.openrouterApiKey) {
        localStorage.setItem('openrouter_api_key', settings.openrouterApiKey);
        // Event für andere Komponenten
        window.dispatchEvent(new CustomEvent('openrouterApiKeyUpdated', { 
          detail: { apiKey: settings.openrouterApiKey } 
        }));
      }
    } catch (error) {
      console.error('Fehler beim Speichern des API Keys:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Fehler beim Speichern des API Keys';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleProfileChange = (field, value) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSettingsChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleApiKeyAdd = () => {
    if (newApiKey.name && newApiKey.key) {
      const apiKey = {
        id: Date.now(),
        name: newApiKey.name,
        key: newApiKey.key.replace(/(.{4}).*(.{4})/, '$1***...***$2'),
        created: new Date().toISOString(),
        lastUsed: null,
        isActive: true,
      };
      
      setSettings(prev => ({
        ...prev,
        apiKeys: [...prev.apiKeys, apiKey],
      }));
      
      setNewApiKey({ name: '', key: '' });
      setApiKeyDialogOpen(false);
    }
  };

  const handleApiKeyDelete = (id) => {
    if (window.confirm('Möchten Sie diesen API-Schlüssel wirklich löschen?')) {
      setSettings(prev => ({
        ...prev,
        apiKeys: prev.apiKeys.filter(key => key.id !== id),
      }));
    }
  };

  const TabPanel = ({ children, value, index, ...other }) => (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              Einstellungen
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Verwalten Sie Ihre Kontoeinstellungen und Systemkonfiguration
            </Typography>
          </Box>
          
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            sx={{
              background: 'linear-gradient(135deg, #FFD700 0%, #F57F17 100%)',
              color: '#000000',
              fontWeight: 600,
              px: 3,
              borderRadius: 2,
              textTransform: 'none',
            }}
          >
            {saving ? 'Speichere...' : 'Speichern'}
          </Button>
        </Box>
        
        {saving && <LinearProgress sx={{ mb: 2 }} />}
      </Box>

      {/* Settings Tabs */}
      <Card sx={{ borderRadius: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab
              icon={<PersonIcon />}
              label="Profil"
              iconPosition="start"
            />
            <Tab
              icon={<SecurityIcon />}
              label="Sicherheit"
              iconPosition="start"
            />
            <Tab
              icon={<ApiIcon />}
              label="OpenRouter"
              iconPosition="start"
            />
            <Tab
              icon={<NotificationsIcon />}
              label="Benachrichtigungen"
              iconPosition="start"
            />
            <Tab
              icon={<ApiIcon />}
              label="API-Schlüssel"
              iconPosition="start"
            />
            <Tab
              icon={<StorageIcon />}
              label="System"
              iconPosition="start"
            />
          </Tabs>
        </Box>

        {/* Profile Settings */}
        <TabPanel value={activeTab} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Avatar
                  sx={{
                    width: 120,
                    height: 120,
                    mx: 'auto',
                    mb: 2,
                    backgroundColor: '#FFD700',
                    color: '#000000',
                    fontSize: '2rem',
                    fontWeight: 700,
                  }}
                >
                  {user?.name?.charAt(0)?.toUpperCase() || 'A'}
                </Avatar>
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => setProfileDialogOpen(true)}
                  sx={{
                    borderColor: '#FFD700',
                    color: '#FFD700',
                    fontWeight: 600,
                    borderRadius: 2,
                    '&:hover': {
                      borderColor: '#F57F17',
                      backgroundColor: 'rgba(255, 215, 0, 0.1)',
                    },
                  }}
                >
                  Profil bearbeiten
                </Button>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={8}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Name"
                    value={profileData.name}
                    onChange={(e) => handleProfileChange('name', e.target.value)}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="E-Mail"
                    type="email"
                    value={profileData.email}
                    onChange={(e) => handleProfileChange('email', e.target.value)}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Unternehmen"
                    value={profileData.company}
                    onChange={(e) => handleProfileChange('company', e.target.value)}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Telefon"
                    value={profileData.phone}
                    onChange={(e) => handleProfileChange('phone', e.target.value)}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Sprache</InputLabel>
                    <Select
                      value={settings.language}
                      onChange={(e) => handleSettingsChange('language', e.target.value)}
                      label="Sprache"
                    >
                      <MenuItem value="de">Deutsch</MenuItem>
                      <MenuItem value="en">English</MenuItem>
                      <MenuItem value="fr">Français</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Zeitzone</InputLabel>
                    <Select
                      value={settings.timezone}
                      onChange={(e) => handleSettingsChange('timezone', e.target.value)}
                      label="Zeitzone"
                    >
                      <MenuItem value="Europe/Berlin">Europa/Berlin</MenuItem>
                      <MenuItem value="Europe/London">Europa/London</MenuItem>
                      <MenuItem value="America/New_York">Amerika/New York</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Security Settings */}
        <TabPanel value={activeTab} index={1}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                Passwort ändern
              </Typography>
              <Button
                variant="outlined"
                startIcon={<LockIcon />}
                onClick={() => setPasswordDialogOpen(true)}
                sx={{
                  borderColor: '#FFD700',
                  color: '#FFD700',
                  fontWeight: 600,
                  borderRadius: 2,
                  '&:hover': {
                    borderColor: '#F57F17',
                    backgroundColor: 'rgba(255, 215, 0, 0.1)',
                  },
                }}
              >
                Passwort ändern
              </Button>
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                Zwei-Faktor-Authentifizierung
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.twoFactorAuth}
                    onChange={(e) => handleSettingsChange('twoFactorAuth', e.target.checked)}
                    color="primary"
                  />
                }
                label="Zwei-Faktor-Authentifizierung aktivieren"
                sx={{ mb: 2 }}
              />
              <Typography variant="body2" color="text.secondary">
                Erhöhen Sie die Sicherheit Ihres Kontos mit einer zusätzlichen Authentifizierungsebene.
              </Typography>
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Session-Timeout</InputLabel>
                <Select
                  value={settings.sessionTimeout}
                  onChange={(e) => handleSettingsChange('sessionTimeout', e.target.value)}
                  label="Session-Timeout"
                >
                  <MenuItem value={15}>15 Minuten</MenuItem>
                  <MenuItem value={30}>30 Minuten</MenuItem>
                  <MenuItem value={60}>1 Stunde</MenuItem>
                  <MenuItem value={120}>2 Stunden</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.loginNotifications}
                    onChange={(e) => handleSettingsChange('loginNotifications', e.target.checked)}
                    color="primary"
                  />
                }
                label="Login-Benachrichtigungen"
                sx={{ mt: 2 }}
              />
            </Grid>
          </Grid>
        </TabPanel>

        {/* OpenRouter Settings */}
        <TabPanel value={activeTab} index={2}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                OpenRouter API Konfiguration
              </Typography>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>OpenRouter API Key:</strong> Konfigurieren Sie hier Ihren globalen OpenRouter API Key. 
                  Dieser wird für alle Bots verwendet, die OpenRouter-Modelle verwenden.
                </Typography>
              </Alert>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="OpenRouter API Key"
                type="password"
                value={settings.openrouterApiKey || ''}
                onChange={(e) => handleSettingsChange('openrouterApiKey', e.target.value)}
                placeholder="sk-or-v1-..."
                helperText="Ihr OpenRouter API Key für alle Bots mit OpenRouter-Modellen"
                sx={{ mb: 2 }}
              />
              <Button
                variant="contained"
                onClick={handleSaveOpenRouterApiKey}
                disabled={!settings.openrouterApiKey || saving}
                sx={{ mb: 2 }}
              >
                {saving ? 'Speichern...' : 'Globalen API Key speichern'}
              </Button>
            </Grid>
            
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Hinweis:</strong> Der API Key wird automatisch aus den Umgebungsvariablen geladen und 
                    zentral in der Datenbank gespeichert. Er ist für alle OpenRouter-Bots verfügbar und kann hier bearbeitet werden.
                  </Typography>
                </Alert>
              </Grid>
            
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Verfügbare OpenRouter-Modelle
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Diese Modelle stehen für Ihre Bots zur Verfügung:
              </Typography>
              
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Chip label="GPT-3.5 Turbo" color="primary" variant="outlined" />
                <Chip label="GPT-4" color="primary" variant="outlined" />
                <Chip label="Claude 3" color="secondary" variant="outlined" />
                <Chip label="Llama 2" color="default" variant="outlined" />
                <Chip label="Gemini Pro" color="info" variant="outlined" />
                <Chip label="Und viele mehr..." color="default" variant="outlined" />
              </Box>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Notification Settings */}
        <TabPanel value={activeTab} index={3}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                E-Mail Benachrichtigungen
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.emailNotifications}
                    onChange={(e) => handleSettingsChange('emailNotifications', e.target.checked)}
                    color="primary"
                  />
                }
                label="E-Mail Benachrichtigungen aktivieren"
                sx={{ mb: 2 }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.weeklyReports}
                    onChange={(e) => handleSettingsChange('weeklyReports', e.target.checked)}
                    color="primary"
                  />
                }
                label="Wöchentliche Berichte"
                sx={{ mb: 2 }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.systemAlerts}
                    onChange={(e) => handleSettingsChange('systemAlerts', e.target.checked)}
                    color="primary"
                  />
                }
                label="System-Warnungen"
                sx={{ mb: 2 }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.pushNotifications}
                    onChange={(e) => handleSettingsChange('pushNotifications', e.target.checked)}
                    color="primary"
                  />
                }
                label="Push-Benachrichtigungen"
                sx={{ mb: 2 }}
              />
            </Grid>
          </Grid>
        </TabPanel>


        {/* System Settings */}
        <TabPanel value={activeTab} index={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Datenaufbewahrung (Tage)</InputLabel>
                <Select
                  value={settings.dataRetention}
                  onChange={(e) => handleSettingsChange('dataRetention', e.target.value)}
                  label="Datenaufbewahrung (Tage)"
                >
                  <MenuItem value={30}>30 Tage</MenuItem>
                  <MenuItem value={90}>90 Tage</MenuItem>
                  <MenuItem value={180}>180 Tage</MenuItem>
                  <MenuItem value={365}>1 Jahr</MenuItem>
                  <MenuItem value={730}>2 Jahre</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Log-Level</InputLabel>
                <Select
                  value={settings.logLevel}
                  onChange={(e) => handleSettingsChange('logLevel', e.target.value)}
                  label="Log-Level"
                >
                  <MenuItem value="debug">Debug</MenuItem>
                  <MenuItem value="info">Info</MenuItem>
                  <MenuItem value="warning">Warning</MenuItem>
                  <MenuItem value="error">Error</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.autoBackup}
                    onChange={(e) => handleSettingsChange('autoBackup', e.target.checked)}
                    color="primary"
                  />
                }
                label="Automatische Backups aktivieren"
                sx={{ mb: 2 }}
              />
            </Grid>
            
            {settings.autoBackup && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Backup-Häufigkeit</InputLabel>
                  <Select
                    value={settings.backupFrequency}
                    onChange={(e) => handleSettingsChange('backupFrequency', e.target.value)}
                    label="Backup-Häufigkeit"
                  >
                    <MenuItem value="daily">Täglich</MenuItem>
                    <MenuItem value="weekly">Wöchentlich</MenuItem>
                    <MenuItem value="monthly">Monatlich</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        </TabPanel>
      </Card>

      {/* Profile Dialog */}
      <Dialog
        open={profileDialogOpen}
        onClose={() => setProfileDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
          },
        }}
      >
        <DialogTitle>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Profil bearbeiten
          </Typography>
        </DialogTitle>
        
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name"
                value={profileData.name}
                onChange={(e) => handleProfileChange('name', e.target.value)}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="E-Mail"
                type="email"
                value={profileData.email}
                onChange={(e) => handleProfileChange('email', e.target.value)}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Unternehmen"
                value={profileData.company}
                onChange={(e) => handleProfileChange('company', e.target.value)}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Telefon"
                value={profileData.phone}
                onChange={(e) => handleProfileChange('phone', e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={() => setProfileDialogOpen(false)}
            sx={{ color: 'text.secondary', fontWeight: 600 }}
          >
            Abbrechen
          </Button>
          <Button
            onClick={() => {
              updateUser(profileData);
              setProfileDialogOpen(false);
            }}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #FFD700 0%, #F57F17 100%)',
              color: '#000000',
              fontWeight: 600,
              px: 3,
              borderRadius: 2,
              textTransform: 'none',
            }}
          >
            Speichern
          </Button>
        </DialogActions>
      </Dialog>

      {/* Password Dialog */}
      <Dialog
        open={passwordDialogOpen}
        onClose={() => setPasswordDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
          },
        }}
      >
        <DialogTitle>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Passwort ändern
          </Typography>
        </DialogTitle>
        
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Aktuelles Passwort"
                type="password"
                value={passwordData.currentPassword}
                onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Neues Passwort"
                type="password"
                value={passwordData.newPassword}
                onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Neues Passwort bestätigen"
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={() => setPasswordDialogOpen(false)}
            sx={{ color: 'text.secondary', fontWeight: 600 }}
          >
            Abbrechen
          </Button>
          <Button
            onClick={() => setPasswordDialogOpen(false)}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #FFD700 0%, #F57F17 100%)',
              color: '#000000',
              fontWeight: 600,
              px: 3,
              borderRadius: 2,
              textTransform: 'none',
            }}
          >
            Passwort ändern
          </Button>
        </DialogActions>
      </Dialog>

      {/* API Key Dialog */}
      <Dialog
        open={apiKeyDialogOpen}
        onClose={() => setApiKeyDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
          },
        }}
      >
        <DialogTitle>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Neuer API-Schlüssel
          </Typography>
        </DialogTitle>
        
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name"
                value={newApiKey.name}
                onChange={(e) => setNewApiKey(prev => ({ ...prev, name: e.target.value }))}
                placeholder="z.B. OpenAI API"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="API-Schlüssel"
                value={newApiKey.key}
                onChange={(e) => setNewApiKey(prev => ({ ...prev, key: e.target.value }))}
                placeholder="sk-..."
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={() => setApiKeyDialogOpen(false)}
            sx={{ color: 'text.secondary', fontWeight: 600 }}
          >
            Abbrechen
          </Button>
          <Button
            onClick={handleApiKeyAdd}
            variant="contained"
            disabled={!newApiKey.name || !newApiKey.key}
            sx={{
              background: 'linear-gradient(135deg, #FFD700 0%, #F57F17 100%)',
              color: '#000000',
              fontWeight: 600,
              px: 3,
              borderRadius: 2,
              textTransform: 'none',
            }}
          >
            Hinzufügen
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar für Benachrichtigungen */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Settings;
